import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { Brain, TrendingDown, Timer, Heart, ArrowRight } from 'lucide-react';

interface ActivityReportProps {
  initialStressLevel: number;
  duration: number;
  activityType: string;
  onClose: () => void;
}

export function ActivityReport({ initialStressLevel, duration, activityType, onClose }: ActivityReportProps) {
  const [isProcessing, setIsProcessing] = useState(true);
  const [finalStressLevel] = useState(() => Math.max(initialStressLevel - Math.random() * 30, 20));
  const [heartRateData, setHeartRateData] = useState<number[]>([]);
  const [breathingRateData, setBreathingRateData] = useState<number[]>([]);

  useEffect(() => {
    // Simulate data processing
    const timer = setTimeout(() => {
      setIsProcessing(false);
    }, 7000);

    // Generate simulated heart rate data
    const hrData = Array.from({ length: 20 }, (_, i) => {
      const baseline = 75;
      const trend = -i * 0.5; // Gradually decreasing trend
      const noise = Math.random() * 5;
      return baseline + trend + noise;
    });
    setHeartRateData(hrData);

    // Generate simulated breathing rate data
    const brData = Array.from({ length: 20 }, (_, i) => {
      const baseline = 16;
      const trend = -i * 0.3; // Gradually decreasing trend
      const noise = Math.random() * 2;
      return baseline + trend + noise;
    });
    setBreathingRateData(brData);

    return () => clearTimeout(timer);
  }, []);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        display: false
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      }
    }
  };

  if (isProcessing) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-4"
        >
          <div className="relative">
            <Brain className="w-12 h-12 text-primary animate-pulse" />
            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
          </div>
          <h2 className="text-xl font-semibold">Processing Activity Data</h2>
          <p className="text-muted-foreground">Analyzing your mindfulness session...</p>
          <div className="w-48 h-1 bg-muted rounded-full mx-auto overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 7 }}
            />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto mindful-scrollbar">
      <div className="min-h-screen p-6">
        <div className="max-w-4xl mx-auto space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-2"
          >
            <h2 className="text-2xl font-semibold">Activity Report</h2>
            <p className="text-muted-foreground">{activityType}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {/* Stress Reduction */}
            <div className="theme-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Brain className="w-5 h-5 text-primary" />
                <h3 className="font-medium">Stress Level</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-baseline">
                  <span className="text-3xl font-bold text-destructive">{initialStressLevel}%</span>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  <span className="text-3xl font-bold text-success">{Math.round(finalStressLevel)}%</span>
                </div>
                <div className="flex items-center gap-2 text-success text-sm">
                  <TrendingDown className="w-4 h-4" />
                  <span>{Math.round(initialStressLevel - finalStressLevel)}% reduction</span>
                </div>
              </div>
            </div>

            {/* Duration */}
            <div className="theme-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Timer className="w-5 h-5 text-primary" />
                <h3 className="font-medium">Duration</h3>
              </div>
              <div className="text-3xl font-bold">{duration} min</div>
              <div className="text-sm text-muted-foreground mt-2">Recommended: 10-20 min</div>
            </div>

            {/* Heart Rate */}
            <div className="theme-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Heart className="w-5 h-5 text-primary" />
                <h3 className="font-medium">Average Heart Rate</h3>
              </div>
              <div className="text-3xl font-bold">
                {Math.round(heartRateData.reduce((a, b) => a + b, 0) / heartRateData.length)} BPM
              </div>
              <div className="text-sm text-success mt-2">Optimal relaxation range</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Heart Rate Chart */}
            <div className="theme-card p-6">
              <h3 className="font-medium mb-4">Heart Rate Variation</h3>
              <div className="h-48">
                <Line
                  data={{
                    labels: Array.from({ length: heartRateData.length }, (_, i) => i),
                    datasets: [{
                      data: heartRateData,
                      borderColor: 'hsl(var(--primary))',
                      tension: 0.4
                    }]
                  }}
                  options={chartOptions}
                />
              </div>
            </div>

            {/* Breathing Rate Chart */}
            <div className="theme-card p-6">
              <h3 className="font-medium mb-4">Breathing Rate</h3>
              <div className="h-48">
                <Line
                  data={{
                    labels: Array.from({ length: breathingRateData.length }, (_, i) => i),
                    datasets: [{
                      data: breathingRateData,
                      borderColor: 'hsl(var(--success))',
                      tension: 0.4
                    }]
                  }}
                  options={chartOptions}
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex justify-center"
          >
            <button
              onClick={onClose}
              className="theme-button px-6 py-3 rounded-lg"
            >
              Close Report
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}